import { Box, Chip, Typography } from "@mui/material";
import { forwardRef, memo, useImperativeHandle, useState, useEffect } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { useTranslation } from "react-i18next";
import { ar, enUS } from "date-fns/locale";
import { colors } from "../../../theme/palette";
import typography from "../../../theme/typography";
import { TimePicker } from "@mui/x-date-pickers";
import { useCreateEventContext } from "./cxt";
import { isToday as checkIfTodayDate } from "date-fns";

const DateAndTime = forwardRef((_, ref) => {
  const { state } = useCreateEventContext();
  const { t, i18n } = useTranslation();

  const [date, setDate] = useState<Date>(state.date);
  const [time, setTime] = useState<Date>(state.time);
  const [timeError, setTimeError] = useState<string>("");
  const [duration, setDuration] = useState<number>(state.duration);
  const [isToday, setIsToday] = useState<boolean>(true);
  const [isFormValid, setIsFormValid] = useState<boolean>(true);

  useEffect(() => {
    setIsToday(checkIfTodayDate(date));
  }, [date]);

  useEffect(() => {
    setIsFormValid(timeError === "");
  }, [timeError]);

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      setDate(newDate);
      const isToday = checkIfTodayDate(newDate);
      setIsToday(isToday);

      if (isToday) {
        const currentTime = new Date();
        const selectedTime = new Date(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
          time.getHours(),
          time.getMinutes()
        );

        if (selectedTime < currentTime) {
          setTimeError(t("pages.events.createEvent.pastTime"));
        } else {
          setTimeError("");
        }
      } else {
        setTimeError("");
      }
    }
  };

  const handleTimeChange = (newTime: Date | null) => {
    if (newTime) {
      const currentTime = new Date();
      const selectedTime = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        newTime.getHours(),
        newTime.getMinutes()
      );

      if (isToday && selectedTime < currentTime) {
        setTimeError(t("pages.events.createEvent.pastTime"));
      } else {
        setTimeError("");
        setTime(selectedTime);
      }
    }
  };

  const getFormattedDateTime = () => {
    if (!date || !time) return "";

    const combinedDateTime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes()
    );
    return combinedDateTime.toISOString();
  };

  useImperativeHandle(ref, () => ({
    getFormattedDateTime,
    date,
    time,
    duration,
    isFormValid,
  }));

  const currentLocale = i18n.language === "ar" ? ar : enUS;

  const disablePastDates = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={currentLocale}>
      <Box component="form" sx={{ display: "flex", justifyContent: "space-between" }}>
        <DateCalendar
          sx={{
            border: `1px solid ${colors.Gray100}`,
            padding: "21px",
            borderRadius: "16px",
            margin: 0,
            maxHeight: "none !important",
            height: "fit-content",
            "& .MuiPickersCalendarHeader-labelContainer": {
              margin: "0 auto",
              position: "relative",
              zIndex: 10,
            },
            "& .MuiPickersCalendarHeader-root": {
              position: "relative",
            },
            "& .MuiPickersArrowSwitcher-root": {
              position: "absolute",
              left: 0,
              bottom: 0,
              width: "100%",
              justifyContent: "space-between",
              marginTop: "12px",
              marginBottom: "4px",
              paddingRight: "12px",
            },
            "& .MuiPickersDay-today:not(.Mui-selected)": {
              background: colors.Green25,
              border: "none",
              color: colors.Green100,
            },
          }}
          value={date}
          onChange={handleDateChange}
          shouldDisableDate={disablePastDates}
        />
        <Box sx={{ width: "397px" }}>
          <Typography
            sx={{
              textAlign: "left",
              color: colors.Dark800,
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "20px",
              marginBottom: "10px",
            }}
          >
            {t("pages.events.createEvent.duration")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              marginBottom: "32px",
            }}
          >
            {[30, 45, 60, 90, 120, 135, 150, 165, 180, 195, 210, 225, 240, 255, 270, 285, 300].map((d) => (
              <Chip
                key={d}
                label={d}
                component="button"
                variant="outlined"
                clickable
                onClick={() => setDuration(d)}
                sx={{
                  width: "73px",
                  textAlign: "center",
                  ...(duration === d ? { ...typography.body14Bold } : { ...typography.body14Regular }),
                  color: duration === d ? colors.Green100 : colors.Dark600,
                  border: duration === d ? `1px solid ${colors.Green100}` : `1px solid ${colors.Gray100}`,
                }}
              />
            ))}
          </Box>

          <Typography
            sx={{
              textAlign: "left",
              color: colors.Dark800,
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "20px",
              marginBottom: "10px",
            }}
          >
            {t("pages.events.createEvent.startAt")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              rowGap: "8px",
            }}
          >
            <TimePicker
              value={time}
              ampm={true}
              onChange={handleTimeChange}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  padding: "0 16px",
                  border: "none",
                  borderRadius: "8px",
                  background: timeError ? colors.Red25 : "#F7F8F8",
                },
                "& .MuiInputBase-input": {
                  padding: "11.5px 0",
                },
              }}
            />
            {timeError && (
              <Typography variant="body12Regular" sx={{ color: colors.Red200 }}>
                {timeError}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
});

export default memo(DateAndTime);
